import { readCondition } from '@seen/core/observation-logic/documentUnits'
import { describeCondition } from '@seen/core/observation-logic/evaluate'
import { cn } from '@seen/ui'
import {
  NodeViewContent,
  type NodeViewProps,
  NodeViewWrapper,
  useEditorState,
} from '@tiptap/react'
import type { JSX } from 'react'
import { ConditionalBranchIcon } from '#src/components/rich-text-editor/conditional-display/ConditionalBranchIcon.tsx'
import { CONDITIONAL_DISPLAY_KEY } from '#src/components/rich-text-editor/conditional-display/ConditionalDisplayExtension.ts'
import type { ConditionalSectionOptions } from '#src/components/rich-text-editor/conditional-display/ConditionalSectionNode.ts'
import {
  CONFIGURING_NODE_CLASS,
  useIsNodeConfiguring,
} from '#src/components/rich-text-editor/conditional-display/useIsNodeConfiguring.ts'

// A conditional section. Live it is just its content (hidden by the
// display plugin when its condition fails); in a template it is framed, its
// condition named in a chip that opens its configuration.
export function ConditionalSectionView(props: NodeViewProps): JSX.Element {
  const { isTemplate } = props.extension.options as ConditionalSectionOptions
  const id = props.node.attrs.id as string | null
  const condition = readCondition(props.node.attrs.showWhen)
  const label = props.node.attrs.label as string | null
  // Worded with the note's labels and answer displays when the display
  // plugin is on.
  const describe = useEditorState({
    editor: props.editor,
    selector: ({ editor }) =>
      CONDITIONAL_DISPLAY_KEY.getState(editor.state)?.describe,
  })
  const cycle = useEditorState({
    editor: props.editor,
    selector: ({ editor }) =>
      !!id &&
      !!CONDITIONAL_DISPLAY_KEY.getState(editor.state)?.result.cycles.includes(
        `section:${id}`
      ),
  })

  const configuring = useIsNodeConfiguring(props)

  if (!isTemplate) {
    return (
      <NodeViewWrapper data-conditional-section="">
        <NodeViewContent />
      </NodeViewWrapper>
    )
  }

  const text = condition ? (describe ?? describeCondition)(condition) : 'always'
  return (
    <NodeViewWrapper
      data-conditional-section=""
      data-configuring={configuring ? 'true' : undefined}
      className={cn(
        'my-2 flex w-full flex-col gap-1.5 rounded-md border border-dashed p-2',
        cycle ? 'border-red-400' : 'border-border',
        configuring && cn(CONFIGURING_NODE_CLASS, 'border-transparent')
      )}
    >
      <button
        type="button"
        contentEditable={false}
        onClick={() => {
          const pos = props.getPos()
          if (typeof pos === 'number') {
            props.editor.commands.openNodeConfig(pos)
          }
        }}
        className={cn(
          'flex w-full items-center gap-[3px] rounded-sm border border-dashed px-1.5 py-1 text-left text-xs hover:bg-gray-460 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950',
          cycle ? 'border-red-400 text-red-600' : 'border-border text-stone-600'
        )}
      >
        <ConditionalBranchIcon className="size-3 shrink-0" />
        {label && (
          <>
            <span className="font-semibold">{label}</span>
            <span className={cycle ? undefined : 'text-foreground'}>·</span>
          </>
        )}
        <span>
          Shows when: {text}
          {cycle ? ' (refers to itself)' : ''}
        </span>
      </button>
      <NodeViewContent />
    </NodeViewWrapper>
  )
}
